import log from "loglevel"
import axios from "axios"
import type { AxiosInstance } from "axios"
import { useMsal } from "@wbim/msal-compositional"

interface ScopeConfig {
  scopes: string[]
}

let gitClient: AxiosInstance | null = null
let msGraphClient: AxiosInstance | null = null
let emdClient: AxiosInstance | null = null
let _getAccessToken: ((scope: ScopeConfig) => Promise<string | null>) | null = null

export function createSingletonClient(baseURL: string, scope: ScopeConfig, extraConfig = {}): AxiosInstance {
  const config = {
    baseURL,
    headers: {
      "Cache-Control": "no-cache"
    },
    ...extraConfig
  }
  const instance = axios.create(config)

  instance.interceptors.request.use(
    async function (config) {
      try {
        if (!_getAccessToken) {
          throw new Error("_getAccessToken is not initialized")
        }
        const token = await _getAccessToken(scope)
        if (token === null) {
          throw new Error("Failed to acquire access token")
        }
        // console.log("Interceptor injecting token:", token?.slice(0, 20) + "...")
        if (!config.headers) config.headers = {}
        config.headers.Authorization = `Bearer ${token}`
        return config
      } catch (error) {
        log.error("Error acquiring access token:", error)
        throw error
      }
    },
    function (error) {
      if (error.request) {
        log.error(error.request)
      } else {
        log.error("Error", error.message)
      }
      log.error(error.config)
    }
  )

  return instance
}

export function initAPIClients(appConfig: AppConfig) {
  const msal = useMsal()
  _getAccessToken = msal.getAccessToken

  const baseUrls = {
    git: `https://dev.azure.com/wbim/${appConfig.VITE_ProjId}/_apis/git/repositories/${appConfig.VITE_RepoId}`,
    emd: appConfig.VITE_EMDBase
  }

  const scopes = {
    azureDevOps: { scopes: ["499b84ac-1321-427f-aa17-267ca6975798/user_impersonation"] },
    msGraph: {
      scopes: [
        "https://graph.microsoft.com/User.Read",
        "https://graph.microsoft.com/User.ReadBasic.All",
        "https://graph.microsoft.com/Directory.Read.All"
      ]
    },
    emdScope: { scopes: [appConfig.VITE_EMDScope] }
  }

  gitClient = createSingletonClient(baseUrls.git, scopes.azureDevOps, {
    params: { "api-version": "5.1" }
  })
  msGraphClient = createSingletonClient("https://graph.microsoft.com/v1.0", scopes.msGraph)
  emdClient = createSingletonClient(baseUrls.emd, scopes.emdScope)
}

export { gitClient, msGraphClient, emdClient }
